/**
 * Content relevance for soft-ranking existing Events/Places lists.
 * Early-stage / sparse-social friendly: own saves + editorial (+ proximity)
 * drive ranking; friends’ saves are a light bonus only. Profile interests optional.
 * No new website segments.
 */

import { inferEventCategories } from '../events/eventDisplayFields';
import { EVENT_CATEGORIES } from '../events/eventCategories';
import { editorialPickSortScore } from '../events/eventEditorialPick';
import { PLACE_CATEGORIES, getLocationCategoryLabels } from '../places/placeCategories';
import { distanceMeters } from '../explore/fogReveal';

/** Canonical interest labels = event ∪ place category offerings (Nightlife once). */
export const USER_INTERESTS = Array.from(
  new Set([...EVENT_CATEGORIES, ...PLACE_CATEGORIES])
);

export const EVENT_INTEREST_OPTIONS = [...EVENT_CATEGORIES];
export const PLACE_INTEREST_OPTIONS = [...PLACE_CATEGORIES];

const USER_INTEREST_SET = new Set(USER_INTERESTS);
const USER_INTEREST_BY_TOKEN = new Map(
  USER_INTERESTS.map((label) => [normalizeToken(label), label])
);

/** Prefer signals that work with few users / quiet friend graphs. */
const SCORE_IMPLICIT_CAT = 100;
const SCORE_DECLARED = 80;
const SCORE_OWN_LIKED = 70;
const SCORE_EDITORIAL_UNIT = 25; // × editorialPickSortScore (0–4) → 0–100 (cold-start anchor)
const SCORE_FRIEND_EACH = 12;
const SCORE_FRIEND_CAP = 24; // light social bonus when friend graph is sparse
const SCORE_NEAR_100M = 80;
const SCORE_NEAR_500M = 40;
const SCORE_NEAR_2KM = 15;

function normalizeToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Keep only known category interest strings (stable order from USER_INTERESTS).
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeUserInterests(value) {
  if (!Array.isArray(value)) return [];
  const chosen = new Set();
  value.forEach((item) => {
    const raw = String(item || '').trim();
    if (USER_INTEREST_SET.has(raw)) {
      chosen.add(raw);
      return;
    }
    const mapped = USER_INTEREST_BY_TOKEN.get(normalizeToken(raw));
    if (mapped) chosen.add(mapped);
  });
  return USER_INTERESTS.filter((label) => chosen.has(label));
}

/**
 * Read interests from a profile doc (interests, else legacy personalities).
 * @param {object|null|undefined} profile
 * @returns {string[]}
 */
export function interestsFromProfile(profile) {
  if (!profile || typeof profile !== 'object') return [];
  if (Array.isArray(profile.interests)) {
    return normalizeUserInterests(profile.interests);
  }
  return normalizeUserInterests(profile.personalities);
}

/**
 * Map freeform category text onto a known interest label when it matches.
 * @param {string} value
 * @returns {string[]}
 */
export function mapContentTokenToInterests(value) {
  const mapped = USER_INTEREST_BY_TOKEN.get(normalizeToken(value));
  return mapped ? [mapped] : [];
}

/**
 * @param {object} place
 * @returns {string[]}
 */
export function interestsForPlace(place) {
  if (!place || typeof place !== 'object') return [];
  const found = new Set();
  getLocationCategoryLabels(place).forEach((label) => {
    mapContentTokenToInterests(label).forEach((id) => found.add(id));
  });
  return USER_INTERESTS.filter((label) => found.has(label));
}

/**
 * @param {object} event
 * @returns {string[]}
 */
export function interestsForEvent(event) {
  if (!event || typeof event !== 'object') return [];
  const found = new Set();
  inferEventCategories(event).forEach((label) => {
    mapContentTokenToInterests(label).forEach((id) => found.add(id));
  });
  return USER_INTERESTS.filter((label) => found.has(label));
}

/**
 * Infer taste from events the user already saved (no Profile Interests required).
 * @param {Record<string, unknown>|null|undefined} likedEvents
 * @returns {string[]}
 */
export function implicitInterestsFromLikedEvents(likedEvents) {
  if (!likedEvents || typeof likedEvents !== 'object') return [];
  const found = new Set();
  Object.values(likedEvents).forEach((entry) => {
    interestsForEvent(entry).forEach((id) => found.add(id));
  });
  return USER_INTERESTS.filter((label) => found.has(label));
}

/**
 * Infer taste from liked place ids by looking up places in the loaded catalog.
 * @param {Set<string>|string[]|Record<string, unknown>|null|undefined} likedLocationIds
 * @param {object[]} placesCatalog
 * @returns {string[]}
 */
export function implicitInterestsFromLikedPlaces(likedLocationIds, placesCatalog) {
  const idSet = toIdSet(likedLocationIds);
  if (!idSet.size || !Array.isArray(placesCatalog)) return [];
  const found = new Set();
  placesCatalog.forEach((place) => {
    if (!place?.id || !idSet.has(String(place.id))) return;
    interestsForPlace(place).forEach((id) => found.add(id));
  });
  return USER_INTERESTS.filter((label) => found.has(label));
}

/**
 * @param {Set<string>|string[]|Record<string, unknown>|null|undefined} liked
 * @returns {Set<string>}
 */
export function toIdSet(liked) {
  if (!liked) return new Set();
  if (liked instanceof Set) return new Set([...liked].map(String));
  if (Array.isArray(liked)) return new Set(liked.map(String));
  if (typeof liked === 'object') return new Set(Object.keys(liked).map(String));
  return new Set();
}

function hasLikedId(id, liked) {
  if (!id) return false;
  return toIdSet(liked).has(String(id));
}

function categoryOverlapScore(itemTags, wantedList, weight) {
  if (!wantedList?.length || !itemTags?.length) return 0;
  const wanted = new Set(wantedList);
  let score = 0;
  itemTags.forEach((tag) => {
    if (wanted.has(tag)) score += weight;
  });
  return score;
}

function friendsBoost(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (!n) return 0;
  return Math.min(SCORE_FRIEND_CAP, n * SCORE_FRIEND_EACH);
}

/**
 * Proximity band score (instant distance — no dwell time).
 * @param {object|null|undefined} place
 * @param {{ lng: number, lat: number }|null|undefined} walker
 * @returns {number}
 */
export function proximityScore(place, walker) {
  if (
    !walker ||
    !Number.isFinite(walker.lng) ||
    !Number.isFinite(walker.lat) ||
    !place ||
    !Number.isFinite(place.lng) ||
    !Number.isFinite(place.lat)
  ) {
    return 0;
  }
  const meters = distanceMeters(walker.lng, walker.lat, place.lng, place.lat);
  if (meters <= 100) return SCORE_NEAR_100M;
  if (meters <= 500) return SCORE_NEAR_500M;
  if (meters <= 2000) return SCORE_NEAR_2KM;
  return 0;
}

/**
 * @param {object} place
 * @param {{
 *   interests?: string[],
 *   implicitInterests?: string[],
 *   likedLocationIds?: Set<string>|string[]|Record<string, unknown>|null,
 *   friendLikedCounts?: Record<string, number>|null,
 *   walker?: { lng: number, lat: number }|null,
 * }} [ctx]
 * @returns {number}
 */
export function scorePlaceRelevance(place, ctx = {}) {
  if (!place) return 0;
  const id = place.id != null ? String(place.id) : '';
  const placeTags = interestsForPlace(place);
  const implicit = normalizeUserInterests(ctx.implicitInterests);
  const declared = normalizeUserInterests(ctx.interests);

  let score = 0;
  score += categoryOverlapScore(placeTags, implicit, SCORE_IMPLICIT_CAT);
  score += categoryOverlapScore(placeTags, declared, SCORE_DECLARED);
  if (hasLikedId(id, ctx.likedLocationIds)) score += SCORE_OWN_LIKED;
  if (id && ctx.friendLikedCounts && typeof ctx.friendLikedCounts === 'object') {
    score += friendsBoost(ctx.friendLikedCounts[id]);
  }
  score += proximityScore(place, ctx.walker);
  return score;
}

/**
 * @param {object} event
 * @param {{
 *   interests?: string[],
 *   implicitInterests?: string[],
 *   likedEventIds?: Set<string>|string[]|Record<string, unknown>|null,
 *   friendsGoingByEventId?: Record<string, unknown[]>|null,
 * }} [ctx]
 * @returns {number}
 */
export function scoreEventRelevance(event, ctx = {}) {
  if (!event) return 0;
  const id = event.id != null ? String(event.id) : '';
  const eventTags = interestsForEvent(event);
  const implicit = normalizeUserInterests(ctx.implicitInterests);
  const declared = normalizeUserInterests(ctx.interests);

  let score = 0;
  score += categoryOverlapScore(eventTags, implicit, SCORE_IMPLICIT_CAT);
  score += categoryOverlapScore(eventTags, declared, SCORE_DECLARED);
  if (hasLikedId(id, ctx.likedEventIds)) score += SCORE_OWN_LIKED;
  score += editorialPickSortScore(event.editorialPick, event) * SCORE_EDITORIAL_UNIT;
  if (id && ctx.friendsGoingByEventId && typeof ctx.friendsGoingByEventId === 'object') {
    const going = ctx.friendsGoingByEventId[id];
    score += friendsBoost(Array.isArray(going) ? going.length : 0);
  }
  return score;
}

/**
 * Stable soft-sort: higher score first; ties keep relative order.
 * @param {object[]} places
 * @param {Parameters<typeof scorePlaceRelevance>[1]} ctx
 * @returns {object[]}
 */
export function sortPlacesByRelevance(places, ctx = {}) {
  if (!Array.isArray(places) || places.length < 2) {
    return Array.isArray(places) ? [...places] : [];
  }
  return places
    .map((place, index) => ({
      place,
      index,
      score: scorePlaceRelevance(place, ctx),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((row) => row.place);
}

/**
 * Soft-sort events (e.g. within a calendar day). Featured editorial blocks stay separate.
 * @param {object[]} events
 * @param {Parameters<typeof scoreEventRelevance>[1]} ctx
 * @returns {object[]}
 */
export function sortEventsByRelevance(events, ctx = {}) {
  if (!Array.isArray(events) || events.length < 2) {
    return Array.isArray(events) ? [...events] : [];
  }
  return events
    .map((event, index) => ({
      event,
      index,
      score: scoreEventRelevance(event, ctx),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((row) => row.event);
}
